import { useNavigate, useSearchParams } from '@solidjs/router'
import { Show, createSignal, onCleanup } from 'solid-js'
import { updateConsent } from '../lib/api'
import { useAuth } from '../lib/auth'
import { isDesktop } from '../lib/device'

type Step = 'email' | 'otp' | 'consent' | 'done' | 'transfer'

export default function Signup() {
  const navigate = useNavigate()
  const auth = useAuth()
  const [searchParams] = useSearchParams()

  const [step, setStep] = createSignal<Step>('email')
  const [email, setEmail] = createSignal('')
  // Asked once, on the way in. The venue counter shows this name, so an empty
  // field would put the guest's email local-part on a staff screen instead.
  const [name, setName] = createSignal('')
  const [otp, setOtp] = createSignal('')
  const [consents, setConsents] = createSignal({
    core: true,
    marketing: false,
    geoLocation: false,
    crmSharing: false,
  })
  const [transferToken, setTransferToken] = createSignal('')
  const [transferUrl, setTransferUrl] = createSignal('')
  const [transferStatus, setTransferStatus] = createSignal<'pending' | 'claimed' | 'expired'>('pending')
  const [pollInterval, setPollInterval] = createSignal<any>(null)

  // useSearchParams types a param as string | string[] | undefined.
  const venueParam = searchParams.venue
  const venueSlug: string | null = Array.isArray(venueParam) ? (venueParam[0] ?? null) : (venueParam ?? null)

  async function handleSendOtp(e: Event) {
    e.preventDefault()
    const success = await auth.sendOtp(email())
    if (success) {
      setStep('otp')
    }
  }

  async function handleVerifyOtp(e: Event) {
    e.preventDefault()
    const success = await auth.verifyOtp(email(), otp(), name())
    if (success) {
      setStep('consent')
    }
  }

  async function handleConsent() {
    const c = consents()
    await updateConsent('core', c.core)
    if (c.marketing) await updateConsent('marketing', true)
    if (c.geoLocation) await updateConsent('geoLocation', true)
    if (c.crmSharing) await updateConsent('crmSharing', true)
    setStep('done')
  }

  // After consent, check device and route accordingly
  async function handleDone() {
    if (isDesktop()) {
      // Desktop: create transfer token and show QR
      await createTransfer()
    } else {
      // Mobile: go to pass, carrying the venue from the QR so the guest lands
      // straight on that venue's check-in.
      navigate(venueSlug ? `/pass?venue=${encodeURIComponent(venueSlug)}` : '/pass')
    }
  }

  async function createTransfer() {
    const token = localStorage.getItem('session_token')
    if (!token) return

    const res = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.zipcheck.my'}/v1/session-transfer/create`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = await res.json()
    if (data.token) {
      setTransferToken(data.token)
      setTransferUrl(data.url)
      setStep('transfer')

      // Poll for claim status
      const interval = setInterval(async () => {
        const statusRes = await fetch(
          `${import.meta.env.VITE_API_URL || 'https://api.zipcheck.my'}/v1/session-transfer/status/${data.token}`,
        )
        const statusData = await statusRes.json()
        if (statusData.status === 'claimed') {
          setTransferStatus('claimed')
          clearInterval(interval)
        }
      }, 3000)
      setPollInterval(interval)
    }
  }

  onCleanup(() => {
    if (pollInterval()) clearInterval(pollInterval())
  })

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      {/* Header */}
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-md mx-auto flex items-center gap-3">
          <img src="/logo.svg" alt="ZipCheck" class="h-8" />
          <span class="text-lg font-semibold text-gray-900">ZipCheck</span>
        </div>
      </header>

      <main class="flex-1 flex items-center justify-center p-4">
        <div class="w-full max-w-md">
          <Show when={venueSlug}>
            <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              You're signing up at <strong>{venueSlug}</strong>. Your pass will work at all ZipCheck venues.
            </div>
          </Show>

          {/* Step 1: Email */}
          <Show when={step() === 'email'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h1 class="text-xl font-bold text-gray-900 mb-2">Create your ZipCheck</h1>
              <p class="text-sm text-gray-600 mb-6">One pass, any venue. Check in your items in seconds.</p>

              <form onSubmit={handleSendOtp}>
                <label class="block text-sm font-medium text-gray-700 mb-1">Your name</label>
                <input
                  type="text"
                  placeholder="Alex"
                  maxLength={80}
                  value={name()}
                  onInput={(e) => setName(e.currentTarget.value)}
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base mb-4
                         focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
                         placeholder:text-gray-400"
                />

                <label class="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                <input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={email()}
                  onInput={(e) => setEmail(e.currentTarget.value)}
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base
                         focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
                         placeholder:text-gray-400"
                />

                <Show when={auth.error()}>
                  <p class="mt-2 text-sm text-red-600">{auth.error()}</p>
                </Show>

                <button
                  type="submit"
                  disabled={auth.isLoading() || !email()}
                  class="mt-4 w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                         hover:bg-emerald-700 active:bg-emerald-800
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
                >
                  {auth.isLoading() ? 'Sending...' : 'Continue'}
                </button>
              </form>

              <p class="mt-4 text-xs text-gray-500 text-center">
                By continuing, you agree to our{' '}
                <a href="/terms" class="text-emerald-600 underline">
                  Terms
                </a>{' '}
                and{' '}
                <a href="/privacy" class="text-emerald-600 underline">
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </Show>

          {/* Step 2: OTP Verification */}
          <Show when={step() === 'otp'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <button
                onClick={() => setStep('email')}
                class="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1"
              >
                ← Back
              </button>

              <h1 class="text-xl font-bold text-gray-900 mb-2">Check your email</h1>
              <p class="text-sm text-gray-600 mb-6">
                We sent a 6-digit code to <strong>{email()}</strong>
              </p>

              <form onSubmit={handleVerifyOtp}>
                <label class="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
                <input
                  type="text"
                  inputmode="numeric"
                  maxlength={6}
                  required
                  placeholder="000000"
                  value={otp()}
                  onInput={(e) => setOtp(e.currentTarget.value.replace(/\D/g, ''))}
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg text-base text-center
                         text-xl sm:text-2xl tracking-[0.3em] sm:tracking-[0.5em] font-mono
                         focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
                         placeholder:text-gray-300"
                />

                <Show when={auth.error()}>
                  <p class="mt-2 text-sm text-red-600">{auth.error()}</p>
                </Show>

                <button
                  type="submit"
                  disabled={auth.isLoading() || otp().length !== 6}
                  class="mt-4 w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                         hover:bg-emerald-700 active:bg-emerald-800
                         disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors"
                >
                  {auth.isLoading() ? 'Verifying...' : 'Verify'}
                </button>
              </form>

              <p class="mt-4 text-xs text-gray-500 text-center">
                Didn't receive it?{' '}
                <button onClick={() => auth.sendOtp(email())} class="text-emerald-600 underline">
                  Resend code
                </button>
              </p>
            </div>
          </Show>

          {/* Step 3: Consent */}
          <Show when={step() === 'consent'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h1 class="text-xl font-bold text-gray-900 mb-2">Your privacy choices</h1>
              <p class="text-sm text-gray-600 mb-6">Choose how we can use your data. You can change these anytime.</p>

              <div class="space-y-4">
                {/* Core (required) */}
                <label class="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled={true}
                    class="mt-1 h-5 w-5 rounded border-gray-300 text-emerald-600"
                  />
                  <div>
                    <p class="text-sm font-medium text-gray-900">Wardrobe service</p>
                    <p class="text-xs text-gray-500">Required to check in and out items at venues.</p>
                  </div>
                </label>

                {/* Marketing */}
                <label class="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={consents().marketing}
                    onChange={(e) => setConsents({ ...consents(), marketing: e.currentTarget.checked })}
                    class="mt-1 h-5 w-5 rounded border-gray-300 text-emerald-600"
                  />
                  <div>
                    <p class="text-sm font-medium text-gray-900">Marketing</p>
                    <p class="text-xs text-gray-500">Receive promotional offers and updates from venues.</p>
                  </div>
                </label>

                {/* Geo-location */}
                <label class="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={consents().geoLocation}
                    onChange={(e) => setConsents({ ...consents(), geoLocation: e.currentTarget.checked })}
                    class="mt-1 h-5 w-5 rounded border-gray-300 text-emerald-600"
                  />
                  <div>
                    <p class="text-sm font-medium text-gray-900">Location alerts</p>
                    <p class="text-xs text-gray-500">Get notified when you're near a venue or leave items behind.</p>
                  </div>
                </label>

                {/* CRM sharing */}
                <label class="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={consents().crmSharing}
                    onChange={(e) => setConsents({ ...consents(), crmSharing: e.currentTarget.checked })}
                    class="mt-1 h-5 w-5 rounded border-gray-300 text-emerald-600"
                  />
                  <div>
                    <p class="text-sm font-medium text-gray-900">Venue recognition</p>
                    <p class="text-xs text-gray-500">
                      Allow venues to recognize you across visits for personalized service.
                    </p>
                  </div>
                </label>
              </div>

              <button
                onClick={handleConsent}
                class="mt-6 w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                       hover:bg-emerald-700 active:bg-emerald-800 transition-colors"
              >
                Continue
              </button>
            </div>
          </Show>

          {/* Step 4: Done — Account Setup */}
          <Show when={step() === 'done'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span class="text-3xl">✓</span>
              </div>

              <h1 class="text-xl font-bold text-gray-900 mb-2">Your ZipCheck account is being set up</h1>
              <p class="text-sm text-gray-600 mb-6">
                We're preparing your account. You'll be ready to check in items at any venue.
              </p>

              <div class="space-y-3">
                <Show when={!isDesktop()}>
                  {/* Mobile: go to pass */}
                  <button
                    onClick={() => navigate(venueSlug ? `/pass?venue=${encodeURIComponent(venueSlug)}` : '/pass')}
                    class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                           hover:bg-emerald-700 active:bg-emerald-800 transition-colors"
                  >
                    Continue to your pass
                  </button>
                </Show>

                <Show when={isDesktop()}>
                  {/* Desktop: continue to transfer */}
                  <button
                    onClick={handleDone}
                    class="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg
                           hover:bg-emerald-700 active:bg-emerald-800 transition-colors"
                  >
                    Continue on your phone
                  </button>
                </Show>

                <button
                  onClick={() => navigate('/')}
                  class="w-full py-3 px-4 bg-white text-gray-700 font-medium rounded-lg
                         border border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  Skip for now
                </button>
              </div>
            </div>
          </Show>

          {/* Step 5: Transfer to Mobile */}
          <Show when={step() === 'transfer'}>
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
              <Show when={transferStatus() === 'pending'}>
                <div class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span class="text-3xl">📱</span>
                </div>

                <h1 class="text-xl font-bold text-gray-900 mb-2">Continue on your phone</h1>
                <p class="text-sm text-gray-600 mb-6">
                  Scan this QR code with your phone camera to continue setting up your ZipCheck pass.
                </p>

                {/* QR Code */}
                <div class="w-48 h-48 bg-white border-2 border-gray-200 rounded-lg mx-auto mb-4 flex items-center justify-center">
                  <p class="text-xs text-gray-400">QR Code</p>
                </div>

                {/* Manual code — break-all + smaller tracking on narrow screens.
                    A token rendered at text-2xl with 0.3em tracking has no natural
                    break point; without these it overflows the card on a 320-360px
                    phone (card #946 "signup layout is clipped"). */}
                <p class="text-xs text-gray-500 mb-2">Or enter this code on your phone:</p>
                <div class="bg-gray-50 rounded-lg p-3 mb-4 overflow-hidden">
                  <p class="text-xl sm:text-2xl font-mono font-bold tracking-[0.15em] sm:tracking-[0.3em] text-gray-900 break-all text-center">
                    {transferToken()}
                  </p>
                </div>

                <p class="text-xs text-gray-400">
                  Code expires in 10 minutes.{' '}
                  <button onClick={createTransfer} class="text-emerald-600 underline">
                    Generate new code
                  </button>
                </p>
              </Show>

              <Show when={transferStatus() === 'claimed'}>
                <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span class="text-3xl">✓</span>
                </div>

                <h1 class="text-xl font-bold text-gray-900 mb-2">Transferred!</h1>
                <p class="text-sm text-gray-600 mb-6">
                  Your account is now active on your phone. You can close this window.
                </p>
              </Show>
            </div>
          </Show>
        </div>
      </main>
    </div>
  )
}
